import React, { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, CircularProgress, Grid, Typography
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import { formatErrorFieldMessages } from 'interfaces'
import {
  IconButtonWithTooltip,
  cardStyles,
  LeadershipOfficerBasicInfoFields,
  UsAddressFields,
  usAddressIsValid,
  useGlobalMessageHandler,
  useUsStateLuts
} from 'components'
import { useNavigate, useParams } from 'react-router-dom'
import { type BaseAddress, type LeadershipAddress, createLeadershipOfficer } from 'services'
import { filterBuildingsForLeadership, getBasicLeadershipOfficerInfo, getEmptyLeadershipAddress, type LeadershipOfficerCreationInfo } from 'helpers'

export const LeadershipOfficerCreateCard: React.FC = () => {
  console.log('LeadershipOfficerCreateCard rendering')
  const params = useParams()
  console.log('LeadershipOfficerCreateCard params:', params)

  const { lutValues: stateLut } = useUsStateLuts()
  const [officerBasicInfo, setOfficerBasicInfo] = useState<LeadershipOfficerCreationInfo>(getBasicLeadershipOfficerInfo(stateLut.states))
  const [loadingClick, setLoadingClick] = useState(false)
  const [errorFields, setErrorFields] = useState<string[]>([])
  const navigate = useNavigate()

  const { showSuccessMessage, showErrorMessage, clearMessages } = useGlobalMessageHandler()

  useEffect(() => {
    console.log('LeadershipOfficerCreateCard useEffect running')
  }, [])

  useEffect(() => {
    setOfficerBasicInfo(prev => prev.state == null ? { ...prev, state: stateLut.states.get(0) ?? null } : prev)
  }, [stateLut.states])

  const toggleLoadingClick = (): void => {
    setLoadingClick(prevState => !prevState)
  }

  const handlePostClick = (): void => {
    console.log('handlePostClick called with data:', officerBasicInfo)
    clearMessages()
    if (!usAddressIsValid(officerBasicInfo.address, showErrorMessage)) return
    toggleLoadingClick()
    void createLeadershipOfficer(officerBasicInfo).then(value => {
      console.log('createLeadershipOfficer response:', value)
      if (value.succeeded && ((value.result?.id) != null)) {
        showSuccessMessage('Leadership Officer has been successfully created!')
        navigate(`/us/leadershipOfficers/edit/${value.result.id}`)
      } else {
        showErrorMessage(formatErrorFieldMessages(value.errors))
        if (value.errors != null) {
          setErrorFields(Object.entries(value.errors).map(([key, value]) => key))
        }
      }
    }).catch((error) => {
      console.error('createLeadershipOfficer error:', error)
      showErrorMessage('Failed to create Leadership Officer')
    }).finally(() => {
      toggleLoadingClick()
    })
  }

  const isFieldEmpty = (key: string): boolean => {
    const value = officerBasicInfo[key as keyof LeadershipOfficerCreationInfo]
    return value === null || value === undefined || value === ''
  }

  const updateAddress = (updated: BaseAddress): void => {
    setOfficerBasicInfo(prevState => ({ ...prevState, address: updated as LeadershipAddress }))
  }

  const resetAddress = (): void => {
    setOfficerBasicInfo(prevState => ({ ...prevState, address: getEmptyLeadershipAddress(0) }))
  }

  return (
    <Card sx={cardStyles}>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Leadership Officer Information</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {loadingClick && <CircularProgress size={24} />}
              <IconButtonWithTooltip
                icon={<SaveIcon />}
                label="Save"
                color='primary'
                onClick={handlePostClick}
                disabled={loadingClick}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <LeadershipOfficerBasicInfoFields
              loading={false}
              disabled={false}
              officer={officerBasicInfo}
              updateValue={setOfficerBasicInfo}
              isFieldEmpty={isFieldEmpty}
              errorFields={errorFields}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Primary Address</Typography>
            <UsAddressFields
              comAddress={officerBasicInfo.address}
              updateAddress={updateAddress}
              resetAddress={resetAddress}
              disabled={loadingClick}
              showPhones={false}
              filterBuildings={(buildings) => filterBuildingsForLeadership(buildings, officerBasicInfo)}
              showSecondaryBuildingButton
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
